import { Component } from '@angular/core';

import {
    DOC_TYPES, INPUT_TYPES, InputDocType, L2_PROCESSES, Landscape, gapKey, landscapeLabel
} from '~/models/document-finder.models';
import { DocumentFinderService } from '~/services/document-finder.service';

export type CellDotState = 'on' | 'rep' | 'nap' | 'miss';

export interface GapView { key: string; type: InputDocType; land: Landscape; l2: string; }

@Component({
    selector: 'df-coverage-rail',
    templateUrl: './df-coverage-rail.component.html',
    styleUrls: ['./df-coverage-rail.component.scss'],
    standalone: false
})
export class DfCoverageRailComponent {
    public get types() { return INPUT_TYPES.filter(type => this.df.scope.types.has(type)); }
    public readonly landscapes: Landscape[] = ['asis', 'tobe'];
    public readonly docTypes = DOC_TYPES as Record<string, (typeof DOC_TYPES)[keyof typeof DOC_TYPES]>;
    public readonly l2 = L2_PROCESSES;

    constructor(public df: DocumentFinderService) {}

    public readonly trackByKey = (_: number, g: GapView) => g.key;

    public get l2InScope() {
        return this.l2.filter(x => this.df.scope.l2.has(x.id));
    }

    public dotState(t: InputDocType, land: Landscape, l2: string): CellDotState {
        const key = gapKey(t, land, l2);
        if (this.df.covered(t, land, l2)) { return 'on'; }
        if (this.df.isReportedMissing(key)) { return 'rep'; }
        if (this.df.notApplicable.has(key)) { return 'nap'; }
        return 'miss';
    }

    public dotTitle(state: CellDotState, l2: string): string {
        switch (state) {
            case 'on': return `${l2}: found`;
            case 'rep': return `${l2}: reported missing`;
            case 'nap': return `${l2}: marked not applicable`;
            default: return `${l2}: nothing found. Click to report a missing document`;
        }
    }

    public cellHasGap(t: InputDocType, land: Landscape): boolean {
        return this.l2.some(x => this.df.isOpenGap(t, land, x.id));
    }

    public get gaps(): GapView[] {
        return this.df.gaps().map(key => {
            const [type, land, l2] = key.split('|') as [InputDocType, Landscape, string];
            return { key, type, land, l2 };
        });
    }

    public landLabel(land: Landscape, long: boolean = false): string {
        return landscapeLabel(land, long);
    }

    public subLabel(t: InputDocType): string {
        return t === 'flow' ? 'LS-210 → BJ8' : t === 'cdd' ? 'To-Be: CDD only' : '';
    }

    public report(t: InputDocType, land: Landscape, l2: string): void {
        this.df.openMissing(gapKey(t, land, l2));
    }

    public reportKey(key: string): void {
        this.df.openMissing(key);
    }

    public markNa(key: string): void {
        this.df.markNotApplicable(key);
        this.df.toast('Marked not applicable');
    }

    public reportAny(): void {
        this.df.openMissing();
    }
}
